import React from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceResultChip } from '@/components/AbsenceApplication/AbsenceResultChip';
import {
  AbsenceStateMode,
  useAbsenceState,
} from '@/components/AbsenceApplication/hooks/useAbsenceState';
import { AbsenceResultChipKind } from '@/types/components';
import type { NonAttendanceApplicationDetail } from '@/components/AbsenceApplication/types';
import { Alert } from '@/components/Icons/Alert';
import { CheckCircleFilled } from '@/components/Icons/CheckCircleFilled';
import { Retry } from '@/components/Icons/Retry';

const { Info, Warning, Error: ChipError } = AbsenceResultChipKind;

interface Props {
  application: NonAttendanceApplicationDetail;
  isRetrying: boolean;
  onRetry: () => void;
}

export const AbsenceResultCard: React.FC<Props> = ({
  application,
  isRetrying,
  onRetry,
}) => {
  const { t } = useTranslation();
  const {
    absenceLabel,
    cateringLabel,
    isRiga,
    isCancellationError,
    showCateringDivider,
    showCateringPreserved,
    showCateringSuccess,
    showRetry,
    showSentTo,
    periodsMatch,
  } = useAbsenceState(application, AbsenceStateMode.Result);

  return (
    <div className="flex w-full flex-col gap-4 rounded-xl bg-card p-4 shadow-card">
      <div className="flex flex-col items-start gap-2">
        <div className="flex w-full items-center gap-1.5">
          <CheckCircleFilled className="size-4 shrink-0" />
          <p className="text-body-16 text-muted-foreground">
            {t('absenceApplication.result.absenceSent')}
          </p>
        </div>
        <AbsenceResultChip kind={Info} label={absenceLabel} />
      </div>

      {showCateringDivider && <div className="h-px w-full bg-border" />}

      {showCateringPreserved && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center gap-1.5">
            <CheckCircleFilled className="size-4 shrink-0" />
            <p className="text-body-14 text-muted-foreground md:text-body-16">
              {t('absenceApplication.result.cateringNeeded')}
            </p>
          </div>
          <AbsenceResultChip kind={Info} label={cateringLabel} />
        </div>
      )}

      {isCancellationError && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-start gap-1.5">
            <Alert className="mt-[2px] size-4 shrink-0 text-destructive" />
            <p className="text-body-14 text-muted-foreground md:text-body-16">
              {t('absenceApplication.result.cateringErrored')}
            </p>
          </div>
          <AbsenceResultChip
            kind={ChipError}
            label={t('absenceApplication.result.cateringGeneralError')}
          />
        </div>
      )}

      {showCateringSuccess && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center gap-1.5">
            <CheckCircleFilled className="size-4 shrink-0" />
            <p className="text-body-14 text-muted-foreground md:text-body-16">
              {t('absenceApplication.result.cateringNotNeeded')}
            </p>
          </div>
          <AbsenceResultChip
            kind={periodsMatch ? Info : Warning}
            label={cateringLabel}
          />
        </div>
      )}

      {showSentTo && isRiga && (
        <p className="text-caption-12 self-end text-subtle-foreground">
          {t('absenceApplication.result.sentToRiga')}
        </p>
      )}
      {showSentTo && !isRiga && (
        <p className="text-caption-12 self-end text-subtle-foreground">
          {t('absenceApplication.result.sentToMily')}
        </p>
      )}
      {showRetry && (
        <>
          <div className="h-px w-full bg-border" />
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="text-subhead-14 flex cursor-pointer items-center justify-center gap-2 font-medium text-primary disabled:cursor-not-allowed"
          >
            <Retry className={`size-4 ${isRetrying ? 'animate-spin' : ''}`} />
            {t('absenceApplication.result.retry')}
          </button>
        </>
      )}
    </div>
  );
};
